import {apiAction, dataAction, staticAction} from "actions/actionWrappers"
import {fetchWorkReadinessScores} from "actions/supplementaryAction"
import {
  emailExistsInSystem,
  fetchSystemStatus,
  getCurrentProfileByGoogle,
  resetPassword,
  sendOtp,
  sendPasswordResetOtp,
  updateProfile,
  verifyOtp,
  verifyOtpForResetOtp
} from "api";
import {accountType, omitNulls, firebaseSignInErrors, isTemporarilyBlockedDueToManyFailedLogins} from "common/helpers";
import {history} from 'common/history';
import i18n from "i18n";
import {fetchUnreadCount} from "api/chatApi";
import {silentApiAction} from "actions/actionWrappers";
import * as firebase from 'firebase/app'
import 'firebase/auth'
import {filter, get, isEmpty, join} from 'lodash'
import {actions} from "reducers/authReducer"
import {actions as profileActions} from "reducers/profileReducer"
import {myFirebase} from "../firebase/firebase"
import {isMatchingOpen} from "business/matching";
import {loginType} from "common/loginType";
import {UserAlreadyExistsError} from "api/errors";
import { changeLanguage, initLanguages } from "actions/languageAction";
import Bugsnag from '@bugsnag/js';
import { cleanupChat } from "actions/chatAction";
import {actions as ChatActions} from "reducers/chatReducer";

const initApp = (noRedirect = false) => async dispatch => {
  const currentPath = history.location.pathname + history.location.search;

  if (currentPath.startsWith("/video/join-meeting")) {
    localStorage.setItem("redirectAfterLogin", currentPath);
  }

  // The flag has to be up before the first await: until it is, isAuthenticated
  // is still false and every protected route redirects to /signin, replacing
  // the URL the reader actually asked for.
  const publicRoutes = ["/calendar/callback", "/sso-login"];
  const willVerify = !publicRoutes.includes(history.location.pathname);
  if (willVerify) {
    dispatch(staticAction(actions.VERIFY_REQUEST));
  }

  // verifyAuth lowers the flag itself once Firebase has answered. Every other
  // way out of here — maintenance, or the status call failing — has to lower it
  // too, or isVerifying stays up and ProtectedRoute shows the splash forever.
  let handedOver = false;
  try {
    const status = await dispatch(apiAction(fetchSystemStatus));
    const isMaintenance = get(status, 'underMaintenance');
    if (isMaintenance) {
      dispatch(dataAction(actions.SET_SYSTEM_STATUS, isMaintenance));
      history.push("/signin")
    } else {
      await dispatch(initLanguages());
      if (willVerify) {
        dispatch(verifyAuth(noRedirect));
        handedOver = true;
      }
    }
  } finally {
    if (willVerify && !handedOver) {
      dispatch(staticAction(actions.VERIFY_SUCCESS));
    }
  }
};

var provider = new firebase.auth.GoogleAuthProvider();

const isSurveyPending = (profile) => isOnlyOneSurveyAvailableForUseAndNotCompleted(profile).length > 1;

const isOnlyOneSurveyAvailableForUseAndNotCompleted = (profile) => {
  const surveys = get(profile, 'status.surveys');
  const usableSurveys = filter(surveys, survey => survey.expPermission === 'use');
  return filter(usableSurveys, survey => survey.surveyStatus.startsWith('NOT_STARTED') || survey.surveyStatus.startsWith('ONGOING'));
};

const isLearningHubUse = (profile) => get(profile, 'status.experiences.session') === 'use';
const isMentorshipUse = (profile) => get(profile, 'status.experiences.curriculum') === 'use';
const isMentorshipShow = (profile) => get(profile, 'status.experiences.curriculum') === 'show';
const hasNewSession = (profile) => get(profile, 'status.newSessionAvailable') === true;


const emailSignIn = (email, password) => async dispatch => {
  Bugsnag.setUser(email);

  try {
    const {data} = await dispatch(apiAction(emailExistsInSystem, email))
    const isAccountTypeGoogle = get(data, "user.provider") === accountType.GOOGLE
    if (isAccountTypeGoogle) {
      await dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, "Our records say that you have signed up for our app via ‘Google login’ authentication. Please use the ‘Sign-in via Google’ option"));
    } else {
      await myFirebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const user = await myFirebase.auth().signInWithEmailAndPassword(email, password);
      const token = await user.user.getIdToken();
      await dispatch(dataAction(actions.ADD_ID_TOKEN, token));
      dispatch(getUserProfileWithGoogleLogin(false));
    }
  } catch (e) {
    if (
      e.code === firebaseSignInErrors.USER_ID_DISABLED ||
      e.code === firebaseSignInErrors.INVALID_EMAIL_ADDRESS
    ) {
      dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, e.message))
    } else if (e.code === firebaseSignInErrors.WRONG_PASSWORD) {
      dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, i18n.t('firebaseWrongEmailPassword')))
    } else if (e.code === firebaseSignInErrors.USER_DOES_NOT_EXIST) {
      dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, i18n.t('firebaseUserDoesNotExist')))
    } else if (isTemporarilyBlockedDueToManyFailedLogins(e)) {
      Bugsnag.notify(e);
      dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, `${i18n.t('firebaseTemporarilyDisabledPart1')} ${i18n.t('firebaseTemporarilyDisabledPart2')}`));
    }  else {
      Bugsnag.notify(e);
      dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, i18n.t('firebaseDefaultError')))
    }
    // dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, "Error with EmailId or Password"));
    console.error('Error', e);
  }
};

const getUserProfileWithGoogleLogin = (noRedirect) => async dispatch => {
  const profile = await dispatch(apiAction(getCurrentProfileByGoogle));
  dispatch(dataAction(profileActions.ADD_DISPLAY_NAME, `${profile.firstName} ${profile.lastName}`));
  await dispatch(dataAction(actions.GOOGLE_LOGIN_SUCCESS, profile));
  dispatch(afterLogin(profile, noRedirect))
  const unread = await dispatch(silentApiAction(fetchUnreadCount));
  dispatch(dataAction(ChatActions.UPDATE_UNREAD_COUNT, unread));
};

const afterLogin = (profile, noRedirect) => async dispatch => {
  dispatch(changeLanguage(get(profile, "newPrimaryLanguage")));
  dispatch(fetchWorkReadinessScores());

  const currentPath = history.location.pathname;
  const videoJoinPath = "/video/join-meeting";

  const legalPages = ["/privacypolicy", "/termsofservice", "/codeofconduct"];
  const signedInRoutes = [
    "/learninghub",
    "/mentorship",
    "/surveylisting",
    "/chat",
    "/internship",
    "/dev",
    videoJoinPath
  ];

  if (signedInRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'))) {
    return;
  }

  if (legalPages.includes(currentPath)) {
    return;
  } else if (!profile?.hasAgreedTerms) {
    dispatch(dataAction(actions.SHOW_PRIVACY_POLICY_POPUP));
  }

  // ✅ 1. Check for redirect from URL query param
  const queryParams = new URLSearchParams(history.location.search);
  const queryRedirect = queryParams.get('redirectUri');
  if (queryRedirect) {
    history.replace(queryRedirect);
    return;
  }

  // ✅ 2. Check for redirect from localStorage
  const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
  if (redirectAfterLogin) {
    localStorage.removeItem('redirectAfterLogin');
    history.push(redirectAfterLogin);
    return;
  }

  if (noRedirect) return;

  // ✅ 3. Existing redirect logic
  const redirectUri = localStorage.getItem("postLoginRedirect");
  if (redirectUri) {
    localStorage.removeItem("postLoginRedirect");

    if (redirectUri.startsWith('/video/join-meeting')) {
      history.push(redirectUri + '?section=video');
    } else {
      history.push(redirectUri);
    }
    return;
  }

  // ✅ 4. Default navigation logic
  if (isOnlyOneSurveyAvailableForUseAndNotCompleted(profile).length === 1) {
    history.push("/survey", { surveyId: isOnlyOneSurveyAvailableForUseAndNotCompleted(profile)[0].surveyId });
  } else if (isSurveyPending(profile)) {
    history.push("/surveylisting");
  } else if (isMentorshipUse(profile) || isMatchingOpen(profile.status)) {
    history.push("/mentorship");
  } else if (isLearningHubUse(profile) && hasNewSession(profile)) {
    history.push("/learninghub");
  } else if (isMentorshipShow(profile)) {
    history.push("/mentorship");
  } else {
    history.push("/learninghub");
  }
};


const googleSignIn = () => async dispatch => {
  dispatch(staticAction(actions.GOOGLE_LOGIN_REQUEST));
  try {
    const user = await myFirebase.auth().signInWithPopup(provider);
    Bugsnag.setUser(user.user.email);
    const token = get(user, 'credential.idToken');
    dispatch(dataAction(actions.ADD_ID_TOKEN, token));
    const result = await dispatch(apiAction(emailExistsInSystem, ''));
    if (result.userExist) {
      dispatch(getUserProfileWithGoogleLogin(false));
    } else {
      await dispatch(dataAction(actions.GOOGLE_LOGIN_SIGNUP_SUCCESS));
      dispatch(dataAction(profileActions.ADD_BASIC_DETAILS_TO_PROFILE, user))
      history.push("/signup");
    }

  } catch (e) {
    Bugsnag.notify(e);
    dispatch(staticAction(actions.GOOGLE_LOGIN_FAILURE))
  }
};

const logoutUser = () => async (dispatch, getState) => {
  await dispatch(cleanupChat());
  const {language} = await getState();
  dispatch(staticAction(actions.LOGOUT_REQUEST));
    myFirebase
      .auth()
      .signOut()
      .then(() => {
        dispatch(changeLanguage(get(language, 'defaultLanguageCode'), false));
        dispatch(staticAction(profileActions.RESET_USER_PROFILE));
        dispatch(staticAction('USER_LOGOUT'));
        dispatch(staticAction(actions.LOGOUT_SUCCESS));
      })
      .catch(error => {
        dispatch(staticAction(actions.LOGOUT_FAILURE));
        console.error('Error', error);
      });
};

// VERIFY_SUCCESS has to wait until the profile has loaded and isAuthenticated
// has flipped. Dispatching it up front left isVerifying false while the user was
// still unauthenticated, so ProtectedRoute redirected to /signin and replaced
// the requested URL — which is why deep links landed on the default screen.
const verifyAuth = (noRedirect) => dispatch => {
  dispatch(staticAction(actions.VERIFY_REQUEST));
  const unsubscribeHandler = myFirebase
    .auth()
    .onAuthStateChanged(async user => {
      unsubscribeHandler();
      try {
        if (user) {
          if (!user.emailVerified) {
            dispatch(dataAction(actions.EMAIL_LOGIN_FAILURE, "Email Not Verified"));
          }
          dispatch(dataAction(profileActions.ADD_DISPLAY_NAME, user.displayName));
          const {userExist} = await dispatch(apiAction(emailExistsInSystem, ''));
          if (userExist) {
            await dispatch(getUserProfileWithGoogleLogin(noRedirect));
          } else {
            await dispatch(dataAction(actions.GOOGLE_LOGIN_SIGNUP_SUCCESS));
            history.push("/signup");
          }
        } else {
          const currentPath = history.location.pathname;
          // `/signin` and `/emailverify` belong here as much as the others do:
          // without them a signed-out reader who opens the sign-up entry point
          // directly, or refreshes part-way through it, is thrown to sign-in.
          const publicRoutes = ["/signin", "/emailverify", "/resetpassword", "/privacypolicy", "/termsofservice", "/codeofconduct", "/user/ncs",  "/ncs-register", "/ncs-login", "/", "/sso-login"];
          if (!publicRoutes.includes(currentPath)) {
            history.push("/signin");
          }
        }
      } finally {
        dispatch(staticAction(actions.VERIFY_SUCCESS));
      }
    });
};

const verifyEmail = (email) => async dispatch => {
  Bugsnag.setUser(email);
  try {
    await dispatch(apiAction(() => sendOtp(email, 6)));
    dispatch(dataAction(actions.SET_EMAIL_OTP_ERROR, false));
    dispatch(dataAction(actions.UPDATE_STEP, 1));
  } catch (e) {
    if (e instanceof UserAlreadyExistsError) {
      dispatch(dataAction(actions.SET_EMAIL_OTP_ERROR, true));
    } else {
      alert('Error while communicating with server. Please try again later or check your network configuration.');
    }
  }
};

const validateOtp = (email, otp) => async dispatch => {
  try {
    await dispatch(apiAction(verifyOtp, {email, otp}));
    dispatch(dataAction(actions.OTP_VERIFIED, {email, otp}));
    dispatch(dataAction(actions.UPDATE_STEP, 2))
  } catch (e) {
    const errorMessage = e?.response?.data?.message || "Something went wrong.";
    dispatch({
      type: actions.OTP_VERIFY_FAILED,
      payload: { error: errorMessage }
    });
  }
};

const setProfilePassword = (password) => dispatch => {
  dispatch(dataAction(actions.SET_PASSWORD, password));
  history.push("/signup");
};


const setNewPassword = (password) => async (dispatch, getState) => {
  const {auth: {email, otp, provider}} = getState();
  try {
    await dispatch(apiAction(resetPassword, {email, otp, password, confirmPassword: password}));
    if (provider === loginType.EMAIL) {
      dispatch(emailSignIn(email, password));
    } else {
      dispatch(initApp());
    }
  } catch (e) {
    console.log("New Password set failed", e)
  }
}

const resetPasswordOtpVerify = (email, otp) => async dispatch => {
  try {
    await dispatch(apiAction(verifyOtpForResetOtp, { email, otp }));
    dispatch(dataAction(actions.OTP_VERIFIED, { email, otp }));
    dispatch(dataAction(actions.RESET_PASSWORD_SCREEN, 2));
  } catch (e) {
    const errorMessage = e?.response?.data?.message || "Something went wrong.";
    dispatch({
      type: actions.OTP_VERIFY_FAILED,
      payload: { error: errorMessage }
    });
  }
};


const sendOTPEmailForPasswordReset = (email) => async dispatch => {
  try {
    const {message, errors} = await dispatch(apiAction(sendPasswordResetOtp, email));

    if (!isEmpty(errors)) {
      const error = join(errors, " AND ");
      dispatch(dataAction(actions.RESET_PASSWORD_ERROR, error));
      return
    }

    dispatch(dataAction(actions.RESET_PASSWORD_ERROR, ""))
    dispatch(dataAction(actions.RESET_OTP_SCREEN_MESSAGE, message))
    dispatch(dataAction(actions.RESET_PASSWORD_SCREEN, 1));
  } catch (e) {
    const error = join(get(e, 'response.data.errors')," AND");
    dispatch(dataAction(actions.RESET_PASSWORD_ERROR, error))
  }
}

const validateEmailAddress = (emailId) => async (dispatch) => {
  const email = emailId.toLowerCase();
  Bugsnag.setUser(email);
  try {
    const {userExist} = await dispatch(apiAction(emailExistsInSystem, email));
    if (userExist) {
      dispatch(sendOTPEmailForPasswordReset(email));
    } else {
      dispatch(dataAction(actions.RESET_PASSWORD_ERROR, "Account with this email does not exist"))
    }
  }
  catch (e) {
    dispatch(dataAction(actions.RESET_PASSWORD_ERROR, "Account with this email does not exist"))
  }
};


const resetErrorMessage = () => dispatch =>{
  dispatch(staticAction(actions.RESET_ERROR_MESSAGE))
}

const saveProfile = (profileToBeSaved) => async(dispatch) => {
  await dispatch(apiAction(updateProfile, omitNulls(profileToBeSaved)));
  await dispatch(dataAction(actions.UPDATE_USER_PROFILE_LOCALLY, profileToBeSaved));
};

const dismissMentorshipFormedDialog = () => dispatch =>
  dispatch(staticAction(actions.DISMISS_MENTORSHIP_FORMED_DIALOG));

export {
  dismissMentorshipFormedDialog,
  emailSignIn,
  googleSignIn,
  logoutUser,
  verifyAuth,
  verifyEmail,
  validateOtp,
  setProfilePassword,
  initApp,
  validateEmailAddress,
  resetPasswordOtpVerify,
  setNewPassword,
  resetErrorMessage,
  saveProfile
};
